/* eslint-disable react/no-unescaped-entities */
import {
  CampaignFormProps,
  CampaignFormRef,
  CampaignFormValue,
  formatCampaignFormValue
} from './CampaignForm.helpers'
import {
  StyledAlert,
  StyledCampaignLandingPageContainer
} from './CampaignForm.styled'
import {
  CampaignStepOverviews,
  CampaignStepSegments,
  CampaignStepSites,
  CampaignStepTemplates
} from './CampaignStep'
import {
  CAMPAIGN_STEP_ITEMS,
  CAMPAIGN_STEP_KEYS,
  CampaignStepHeader
} from './CampaignStepHeader'
import Icon, { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { Button, FormInstance, notification } from 'antd'
import { ItemType } from 'antd/lib/menu/hooks/useItems'
import { Box } from 'components/atoms/box'
import { MainHeader, menuRoutes } from 'components/molecules'
import { CAMPAIGN_STATUS } from 'constants/campaign'
import { useAuth } from 'context/Auth'
import { useSessionStorage } from 'hooks/useSessionStorage'
import { Close, Warning } from 'icons'
import Page404 from 'pages/404'
import {
  Ref,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import { useNavigate } from 'react-router-dom'
import { NotificationServices } from 'services/notification'
import { plausibleAPI } from 'utils/plausibleTracking'

const CampaignFormWithoutRef = (
  props: CampaignFormProps,
  ref?: Ref<CampaignFormRef>
) => {
  const navigate = useNavigate()
  const {
    title,
    notFound,
    loading,
    disabled,
    disabledReady,
    defaultValue,
    value,
    template_type,
    onSave,
    roles,
    permissions
  } = props
  const { userAuth } = useAuth()
  const isChanged = useRef(false)
  const overviews = useRef<FormInstance>(null)
  const sites = useRef<FormInstance>(null)
  const segments = useRef<FormInstance>(null)
  const templates = useRef<FormInstance>(null)
  const [step, setStep] = useSessionStorage(
    '$campaignStep',
    CAMPAIGN_STEP_KEYS.overviews
  )
  const [values, setValues] = useState(defaultValue || value)
  const [valuesChanged, setValuesChanged] = useState(defaultValue || value)
  const [modalOpen, setModalOpen] = useState(false)

  const [isApprovalLoading, setIsApprovalLoading] = useState(false)

  const [isDisplayApprovalFlow, setIsDisplayApprovalFlow] = useState(
    value?.approve_flow &&
      value.approve_flow?.flow_type === 'approve_campaign' &&
      value.approve_flow?.status !== 'reject' &&
      value.approve_flow?.status !== 'approve'
  )

  const disabledSegment = useMemo(() => {
    return ['broadcast'].some(
      (push_type) => push_type === valuesChanged?.push_type
    )
  }, [valuesChanged?.push_type])

  const items = useMemo(() => {
    return CAMPAIGN_STEP_ITEMS.map((stepItem) => {
      let disabled = step <= stepItem.key
      switch (stepItem.key) {
        case CAMPAIGN_STEP_KEYS.sites:
          disabled = !Boolean(valuesChanged?.name)
          break
        case CAMPAIGN_STEP_KEYS.segments:
          disabled =
            disabledSegment ||
            !Boolean(valuesChanged?.site_ids && valuesChanged.site_ids?.length)
          break
        case CAMPAIGN_STEP_KEYS.templates:
          if (disabledSegment) {
            disabled = false
          } else {
            disabled = !Boolean(
              valuesChanged?.segments_ids && valuesChanged.segments_ids?.length
            )
          }
          break
      }
      return { ...stepItem, disabled }
    })
  }, [
    step,
    disabledSegment,
    valuesChanged?.name,
    valuesChanged?.segments_ids,
    valuesChanged?.site_ids
  ])
  const site = useMemo(() => {
    if (valuesChanged?.site_ids && valuesChanged?.site_ids?.[0]) {
      return valuesChanged?.site_ids?.[0]
    }
    return ''
  }, [valuesChanged])
  const okText = useMemo(() => {
    if (step < CAMPAIGN_STEP_KEYS.templates) {
      return 'Next'
    }
    return 'Save'
  }, [step])

  const handleValuesChange = (
    _valChanged: any,
    _valsChanged: CampaignFormValue
  ) => {
    const newValuesChanged = { ...valuesChanged, ..._valsChanged }
    if (
      _valChanged.hasOwnProperty('push_type') &&
      _valChanged.push_type === 'broadcast'
    ) {
      newValuesChanged.segments_ids = undefined
      setValues(newValuesChanged)
    }
    if (
      _valChanged.hasOwnProperty('site_ids') &&
      JSON.stringify(values?.site_ids) !==
        JSON.stringify(newValuesChanged.site_ids)
    ) {
      newValuesChanged.segments_ids = undefined
      newValuesChanged.template_id = undefined
      setValues(newValuesChanged)
    }
    isChanged.current = true
    setValuesChanged(newValuesChanged)
  }
  const handleFinish = useCallback(
    (vals: CampaignFormValue) => {
      const newValues = { ...values, ...vals }
      setValues(newValues)
      setValuesChanged(newValues)
      plausibleAPI.trackEvent('campaign_save', {
        props: {
          campaign_type: newValues?.template_type || '',
          uid: userAuth?.id || '',
          un: userAuth?.name || '',
          ue: userAuth?.email || ''
        }
      })
      if (step === items[items.length - 1].key) {
        isChanged.current = false
        onSave?.({ ...newValues, status: CAMPAIGN_STATUS.ready })
      }
    },
    [items, onSave, step, userAuth?.email, userAuth?.id, userAuth?.name, values]
  )
  const handleChangeStep = useCallback(
    async (stepChanged: number) => {
      if (stepChanged < step) {
        setStep(stepChanged)
        return
      }
      try {
        let vals = {}
        let nextStep = stepChanged
        switch (step) {
          case CAMPAIGN_STEP_KEYS.overviews: {
            vals = await overviews.current?.validateFields()
            break
          }
          case CAMPAIGN_STEP_KEYS.sites: {
            vals = await sites.current?.validateFields()
            if (disabledSegment) {
              nextStep = CAMPAIGN_STEP_KEYS.templates
            }
            break
          }
          case CAMPAIGN_STEP_KEYS.segments: {
            if (!disabledSegment) {
              vals = await segments.current?.validateFields()
            }
            break
          }
          case CAMPAIGN_STEP_KEYS.templates: {
            vals = await templates.current?.validateFields()
            nextStep = CAMPAIGN_STEP_KEYS.templates
            break
          }
        }
        handleFinish(vals)
        setStep(nextStep)
      } catch (error: any) {
        if (error?.errorFields && error?.errorFields?.length) {
          error.errorFields.forEach((errorField: any) => {
            if (errorField?.name && errorField.name?.length) {
              if (errorField.name.includes('site_ids')) {
                sites.current?.submit()
              }
              if (errorField.name.includes('segments_ids')) {
                segments.current?.submit()
              }
              if (errorField.name.includes('template_id')) {
                templates.current?.submit()
              }
            }
          })
        }
      }
      return
    },
    [disabledSegment, handleFinish, step]
  )

  const handleOk = useCallback(
    (nextStep: number) => {
      return async () => {
        await handleChangeStep(nextStep)
      }
    },
    [handleChangeStep]
  )

  const handleOnBack = useCallback(() => {
    if (
      !isChanged.current ||
      JSON.stringify(values) !== JSON.stringify(valuesChanged)
    ) {
      navigate(`${menuRoutes.campaign}`)
      return
    }
    setModalOpen(true)
  }, [navigate, values, valuesChanged])
  const handleModalOnClose = useCallback(() => {
    setModalOpen(false)
  }, [])
  const handleModalOnCancel = () => {
    handleModalOnClose()
    navigate(`${menuRoutes.campaign}`)
  }
  const handleModalOnOk = useCallback(() => {
    onSave?.({ ...valuesChanged, status: CAMPAIGN_STATUS.draft }, true)
    handleModalOnClose()
  }, [handleModalOnClose, onSave, valuesChanged])

  const handleDropdownOnClickItem = (item: ItemType) => {
    if (item?.key === 'draft') {
      const valuesSaveChanged = {
        ...valuesChanged,
        status: CAMPAIGN_STATUS.draft
      }
      isChanged.current = false
      setValues(valuesSaveChanged)
      setValuesChanged(valuesSaveChanged)
      onSave?.(valuesSaveChanged)
    }
  }

  const handleApproveCampaign = useCallback(
    (action: string) => {
      if (value?.approve_flow?.flow_type === 'approve_campaign') {
        setIsApprovalLoading(true)
        NotificationServices.approveFlow(action, { ...value.approve_flow })
          .then((res) => {
            if (res.data.status === 'failed') {
              throw new Error()
            }
            setIsDisplayApprovalFlow(false)
            notification.success({
              message: 'Successfully!'
            })
          })
          .catch(() => {
            notification.error({
              message: 'Something went wrong. Please try again!'
            })
          })
          .finally(() => {
            setIsApprovalLoading(false)
          })
      }
    },
    [value]
  )

  useEffect(() => {
    setValues(value)
    setValuesChanged(value)
  }, [value])

  useImperativeHandle(
    ref,
    () => {
      return {
        overviewsForm: overviews.current,
        sitesForm: sites.current,
        segmentsForm: segments.current,
        templatesForm: templates.current,
        onChangeStep: handleChangeStep,
        modalOnClose: handleModalOnClose
      }
    },
    [handleChangeStep, handleModalOnClose]
  )

  if (notFound) {
    return (
      <MainHeader
        headerComponent={
          <CampaignStepHeader
            onBack={() => navigate(`${menuRoutes.campaign}`)}
            onCancel={() => navigate(`${menuRoutes.campaign}`)}
          />
        }
        bgColor="#F7F9FC">
        <Page404 />
      </MainHeader>
    )
  }

  return (
    <MainHeader
      headerComponent={
        <CampaignStepHeader
          title={title}
          onBack={handleOnBack}
          stepCurrent={step}
          stepItems={items}
          stepOnChange={handleChangeStep}
          modalOpen={modalOpen}
          modalOnClose={handleModalOnClose}
          modalOnCancel={handleModalOnCancel}
          modalOnOk={handleModalOnOk}
          isShowDropdown={!disabledReady ? !disabled : false}
          dropdownMenuItems={[
            {
              key: 'draft',
              label: 'Save changed'
            }
          ]}
          dropdownMenuOnClick={handleDropdownOnClickItem}
          onCancel={handleOnBack}
          okText={okText}
          onOk={handleOk(step + 1)}
          okButtonProps={{
            loading: loading,
            disabled: step === CAMPAIGN_STEP_KEYS.templates ? disabled : false
          }}
        />
      }
      bgColor="#F7F9FC"
      stickyHeader
      roles={roles}
      permissions={permissions}>
      <StyledCampaignLandingPageContainer className={`container_${step}`}>
        {disabled && !loading && !isDisplayApprovalFlow && (
          <StyledAlert
            closable
            closeIcon={<Icon component={Close} />}
            showIcon
            icon={<Icon component={Warning} />}
            type="warning"
            description="Since you have already launched your campaign, you can't edit it."
          />
        )}
        {isDisplayApprovalFlow && (
          <StyledAlert
            showIcon
            type="info"
            description={
              <Box>
                Please review the campaign details carefully and select an
                action to proceed. Choose "Approve" to authorize the campaign or
                "Reject" if it does not meet the required criteria.
              </Box>
            }
            action={
              <Box className="flex gap-2">
                <Button
                  size="small"
                  icon={<CloseOutlined className="text-[12px]" />}
                  onClick={(evt) => {
                    evt.stopPropagation()
                    handleApproveCampaign('reject')
                  }}
                  loading={isApprovalLoading}
                  className="h-[32px] px-3 py-1 border-0 bg-[#FAE9E8] hover:bg-[#FAE9E8] focus:bg-[#FAE9E8] text-[#E64728] hover:text-[#E64728] focus:text-[#E64728]">
                  Reject
                </Button>
                <Button
                  size="small"
                  icon={<CheckOutlined className="text-[12px]" />}
                  onClick={(evt) => {
                    evt.stopPropagation()
                    handleApproveCampaign('approve')
                  }}
                  loading={isApprovalLoading}
                  className="h-[32px] px-3 py-1 border-0 bg-[#E3F2FF] hover:bg-[#E3F2FF] focus:bg-[#E3F2FF] text-[#2D87F3] hover:text-[#2D87F3] focus:text-[#2D87F3]">
                  Approve
                </Button>
              </Box>
            }
          />
        )}
        {Boolean(step === CAMPAIGN_STEP_KEYS.overviews) && (
          <CampaignStepOverviews
            name="overviews"
            ref={overviews}
            loading={loading}
            disabled={disabledReady || disabled}
            template_type={template_type}
            initialValues={values}
            onValuesChange={handleValuesChange}
            onFinish={handleFinish}
          />
        )}
        {Boolean(step === CAMPAIGN_STEP_KEYS.sites) && (
          <CampaignStepSites
            name="sites"
            ref={sites}
            loading={loading}
            disabled={disabledReady || disabled}
            template_type={template_type}
            initialValues={values}
            onValuesChange={handleValuesChange}
            onFinish={handleFinish}
          />
        )}
        {Boolean(step === CAMPAIGN_STEP_KEYS.segments) && (
          <CampaignStepSegments
            name="segments"
            ref={segments}
            site={site}
            loading={loading}
            disabled={disabledReady || disabled}
            template_type={template_type}
            initialValues={values}
            onValuesChange={handleValuesChange}
            onFinish={handleFinish}
          />
        )}
        {Boolean(step === CAMPAIGN_STEP_KEYS.templates) && (
          <CampaignStepTemplates
            name="templates"
            ref={templates}
            site={site}
            loading={loading}
            disabled={!disabledReady ? disabled : false}
            template_type={template_type}
            initialValues={values}
            onValuesChange={handleValuesChange}
            onFinish={handleFinish}
          />
        )}
      </StyledCampaignLandingPageContainer>
    </MainHeader>
  )
}

export type { CampaignFormProps, CampaignFormRef, formatCampaignFormValue }
export const CampaignForm = forwardRef(CampaignFormWithoutRef)
